import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Segment,
  Header,
  Icon,
  Button,
  Grid,
  Label,
  Divider,
  Loader,
  Message,
  Table,
} from "semantic-ui-react";
import { SG_FETCH_LLM_USAGE_LOG } from "../constants/actions";
import { format } from "date-fns";
import styled from "styled-components";

export default function UsageLogDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { llm_usage_log, llm_usage_logs_pending } = useSelector(
    (state) => state.llm_log || {}
  );

  useEffect(() => {
    if (id) {
      dispatch({ type: SG_FETCH_LLM_USAGE_LOG, payload: { id } });
    }
  }, [dispatch, id]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm:ss");
    } catch {
      return dateString;
    }
  };

  if (llm_usage_logs_pending) {
    return (
      <Segment>
        <Loader active inline="centered">
          Loading usage log details...
        </Loader>
      </Segment>
    );
  }

  if (!llm_usage_log) {
    return (
      <Segment>
        <Message warning>
          <Message.Header>Not Found</Message.Header>
          <p>Usage log not found.</p>
        </Message>
      </Segment>
    );
  }

  return (
    <DetailWrapper>
      <Segment>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Header as="h2">
            <Icon name="file alternate" />
            <Header.Content>
              Usage Log Details
              <Header.Subheader>ID: {llm_usage_log.id}</Header.Subheader>
            </Header.Content>
          </Header>
          <Button onClick={() => navigate("/app/llm-usage/usage-logs")}>
            <Icon name="arrow left" />
            Back to List
          </Button>
        </div>

        <Divider />

        <Grid columns={2} stackable>
          <Grid.Row>
            <Grid.Column>
              <InfoSection>
                <h3>
                  <Icon name="info circle" /> Basic Information
                </h3>
                <InfoItem>
                  <label>Model Config:</label>
                  <value>
                    {llm_usage_log.model_config ? (
                      <>
                        <strong>{llm_usage_log.model_config.model_name}</strong>
                        <br />
                        <small>{llm_usage_log.model_config.provider}</small>
                      </>
                    ) : (
                      "N/A"
                    )}
                  </value>
                </InfoItem>
                <InfoItem>
                  <label>Success:</label>
                  <value>
                    <Label color={llm_usage_log.success ? "green" : "red"}>
                      {llm_usage_log.success ? "Yes" : "No"}
                    </Label>
                  </value>
                </InfoItem>
                <InfoItem>
                  <label>Status Code:</label>
                  <value>{llm_usage_log.status_code || "N/A"}</value>
                </InfoItem>
                <InfoItem>
                  <label>Timestamp:</label>
                  <value>{formatDate(llm_usage_log.created_at)}</value>
                </InfoItem>
                <InfoItem>
                  <label>Latency:</label>
                  <value>
                    {llm_usage_log.latency_ms
                      ? `${llm_usage_log.latency_ms}ms`
                      : "N/A"}
                  </value>
                </InfoItem>
                <InfoItem>
                  <label>Request ID:</label>
                  <value>
                    <code>{llm_usage_log.request_id || "N/A"}</code>
                  </value>
                </InfoItem>
                <InfoItem>
                  <label>Endpoint:</label>
                  <value>{llm_usage_log.endpoint || "N/A"}</value>
                </InfoItem>
              </InfoSection>
            </Grid.Column>

            <Grid.Column>
              <InfoSection>
                <h3>
                  <Icon name="user" /> Organization & Employee
                </h3>
                <InfoItem>
                  <label>Organization:</label>
                  <value>
                    {llm_usage_log.organization ? (
                      <Label color="teal">Org #{llm_usage_log.organization}</Label>
                    ) : (
                      "N/A"
                    )}
                  </value>
                </InfoItem>
                <InfoItem>
                  <label>Employee:</label>
                  <value>
                    {llm_usage_log.employee ? (
                      <Label color="blue">Emp #{llm_usage_log.employee}</Label>
                    ) : (
                      "N/A"
                    )}
                  </value>
                </InfoItem>
              </InfoSection>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Divider />

        <InfoSection>
          <h3>
            <Icon name="calculator" /> Token Usage & Cost
          </h3>
          <Table definition>
            <Table.Body>
              <Table.Row>
                <Table.Cell width={4}>Prompt Tokens</Table.Cell>
                <Table.Cell>
                  {llm_usage_log.prompt_tokens?.toLocaleString() || 0}
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Completion Tokens</Table.Cell>
                <Table.Cell>
                  {llm_usage_log.completion_tokens?.toLocaleString() || 0}
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>
                  <strong>Total Tokens</strong>
                </Table.Cell>
                <Table.Cell>
                  <strong>
                    {llm_usage_log.total_tokens?.toLocaleString() || 0}
                  </strong>
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Cost (micro-USD)</Table.Cell>
                <Table.Cell>
                  {llm_usage_log.cost_micro_usd?.toLocaleString() || 0} μUSD
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>
                  <strong>Cost (USD)</strong>
                </Table.Cell>
                <Table.Cell>
                  <strong>
                    ${((llm_usage_log.cost_micro_usd || 0) / 1000000).toFixed(6)}
                  </strong>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </InfoSection>

        {llm_usage_log.meta && Object.keys(llm_usage_log.meta).length > 0 && (
          <>
            <Divider />
            <InfoSection>
              <h3>
                <Icon name="file code" /> Metadata
              </h3>
              <CodeBlock>
                {JSON.stringify(llm_usage_log.meta, null, 2)}
              </CodeBlock>
            </InfoSection>
          </>
        )}
      </Segment>
    </DetailWrapper>
  );
}

const DetailWrapper = styled.div`
  .ui.segment {
    padding: 2rem;
  }
`;

const InfoSection = styled.div`
  margin-bottom: 2rem;

  h3 {
    color: #2185d0;
    margin-bottom: 1rem;
    font-size: 1.2rem;

    i.icon {
      margin-right: 0.5rem;
    }
  }
`;

const InfoItem = styled.div`
  display: flex;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;

  label {
    font-weight: 600;
    width: 140px;
    color: #666;
  }

  value {
    flex: 1;
    color: #333;

    code {
      background: #f5f5f5;
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 0.9em;
    }
  }
`;

const CodeBlock = styled.pre`
  background: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
`;